import { UserGroupIcon, CurrencyDollarIcon, TagIcon, CalendarIcon, ClockIcon } from "@heroicons/react/24/outline";
import { XCircleIcon as XCircleSolidIcon } from "@heroicons/react/24/solid";
import { FC, useCallback, useMemo } from "react";
import { Box, Chip, Stack } from "@mui/material";
import { useLocation } from "react-router-dom";
import i18next from "i18next";
import dayjs from "dayjs";

import Collaborator from "../../../@types/Collaborator";
import { FilterValues } from "../../../@types/filter";
import { Category } from "../../../enums/Category";

interface ActiveFiltersChipsProps {
  filterValues: FilterValues;
  onFilterUpdate: (filterValues: FilterValues) => void;
  onChipRemove?: (filterValues: FilterValues) => void;
  localCollaboratorName: string;
  localCostCenter: string;
  isRefundRequestPage: boolean;
  loggedUser?: Collaborator;
  includeAdvances?: boolean;
}

const ActiveFiltersChips: FC<ActiveFiltersChipsProps> = ({
  filterValues,
  onFilterUpdate,
  onChipRemove,
  isRefundRequestPage,
  loggedUser,
  includeAdvances = false,
}) => {
  const location = useLocation();

  const removeFilter = useCallback(
    (filterKey: string) => {
      if (
        filterKey === "status" &&
        location.pathname.includes("/approvals") &&
        filterValues.statusFilter?.key === "all"
      ) {
        return;
      }

      const newFilterValues = { ...filterValues };

      switch (filterKey) {
        case "collaborator":
          if (isRefundRequestPage) {
            return;
          }
          newFilterValues.collaboratorName = "";
          newFilterValues.collaborator = null;
          break;
        case "owner":
          newFilterValues.ownerName = "";
          break;
        case "costCenter":
          newFilterValues.costCenter = "";
          break;
        case "status":
          newFilterValues.statusFilter = null;
          break;
        case "startDate":
          newFilterValues.startDate = null;
          break;
        case "endDate":
          newFilterValues.endDate = null;
          break;
        case "ownerApprovalDateUntil":
          newFilterValues.ownerApprovedDateUntil = null;
          break;
        case "ownerApprovalDateAfter":
          newFilterValues.ownerApprovedDateAfter = null;
          break;
        case "financialApprovalDateUntil":
          newFilterValues.financialApproveDateUntil = null;
          break;
        case "financialApprovalDateAfter":
          newFilterValues.financialApproveDateAfter = null;
          break;
        case "createDateUntil":
          newFilterValues.createDateUntil = null;
          break;
        case "createDateAfter":
          newFilterValues.createDateAfter = null;
          break;
        case "category":
          newFilterValues.expenseCategory = null;
          break;
        case "type":
          newFilterValues.isAdvance = undefined;
          break;
        case "clientRefund":
          newFilterValues.clientRefund = null;
          break;
      }

      onChipRemove ? onChipRemove(newFilterValues) : onFilterUpdate(newFilterValues);
    },
    [filterValues, onFilterUpdate, onChipRemove, isRefundRequestPage, location.pathname],
  );

  const chips = useMemo(() => {
    const result = [];

    // Verificar se estamos nas páginas de aprovações ou busca
    const isApprovalsOrSearchPage = location.pathname.includes("/approvals") || location.pathname.includes("/search");

    // 1. Colaborador (permanente na página de request, ou se estiver nas páginas de approvals/search)
    if (isRefundRequestPage && loggedUser) {
      result.push({
        key: "collaborator",
        label: loggedUser.name,
        icon: UserGroupIcon,
        permanent: true,
      });
    } else if (filterValues.collaboratorName && filterValues.collaboratorName.trim() && isApprovalsOrSearchPage) {
      result.push({
        key: "collaborator",
        label: filterValues.collaboratorName,
        icon: UserGroupIcon,
        permanent: false,
      });
    }

    // 1.5 Gestor
    if (filterValues.ownerName && filterValues.ownerName.trim() && isApprovalsOrSearchPage) {
      result.push({
        key: "owner",
        label: `Gestor: ${filterValues.ownerName}`,
        icon: UserGroupIcon,
        permanent: false,
      });
    }

    // 2. Centro de Custo
    if (filterValues.costCenter && filterValues.costCenter.trim()) {
      result.push({
        key: "costCenter",
        label: `Centro: ${filterValues.costCenter}`,
        icon: CurrencyDollarIcon,
        permanent: false,
      });
    }

    // 3. Status
    if (filterValues.statusFilter) {
      result.push({
        key: "status",
        label: filterValues.statusFilter.name,
        icon: TagIcon,
        permanent: false,
      });
    }

    // 4. Datas de período
    if (filterValues.startDate) {
      result.push({
        key: "startDate",
        label: `De ${dayjs(filterValues.startDate).format("DD/MM")}`,
        icon: CalendarIcon,
        permanent: false,
      });
    }

    if (filterValues.endDate) {
      result.push({
        key: "endDate",
        label: `Até ${dayjs(filterValues.endDate).format("DD/MM")}`,
        icon: CalendarIcon,
        permanent: false,
      });
    }

    // 5. Datas de aprovação gestor
    if (filterValues.ownerApprovedDateUntil) {
      result.push({
        key: "ownerApprovalDateUntil",
        label: `Gestor aprovado até ${dayjs(filterValues.ownerApprovedDateUntil).format("DD/MM")}`,
        icon: ClockIcon,
        permanent: false,
      });
    }

    if (filterValues.ownerApprovedDateAfter) {
      result.push({
        key: "ownerApprovalDateAfter",
        label: `Gestor aprovado após ${dayjs(filterValues.ownerApprovedDateAfter).format("DD/MM")}`,
        icon: ClockIcon,
        permanent: false,
      });
    }

    // 6. Datas de aprovação financeiro
    if (filterValues.financialApproveDateUntil) {
      result.push({
        key: "financialApprovalDateUntil",
        label: `Fin. aprovado até ${dayjs(filterValues.financialApproveDateUntil).format("DD/MM")}`,
        icon: ClockIcon,
        permanent: false,
      });
    }

    if (filterValues.financialApproveDateAfter) {
      result.push({
        key: "financialApprovalDateAfter",
        label: `Fin. aprovado após ${dayjs(filterValues.financialApproveDateAfter).format("DD/MM")}`,
        icon: ClockIcon,
        permanent: false,
      });
    }

    // 7. Datas de criação
    if (filterValues.createDateUntil) {
      result.push({
        key: "createDateUntil",
        label: `Criado até ${dayjs(filterValues.createDateUntil).format("DD/MM")}`,
        icon: ClockIcon,
        permanent: false,
      });
    }

    if (filterValues.createDateAfter) {
      result.push({
        key: "createDateAfter",
        label: `Criado após ${dayjs(filterValues.createDateAfter).format("DD/MM")}`,
        icon: ClockIcon,
        permanent: false,
      });
    }

    // 9. Categoria de despesa
    if (filterValues.expenseCategory) {
      const categoryKey = Object.values(Category).find((cat) => cat === filterValues.expenseCategory);
      const label = categoryKey ? i18next.t(`category.${categoryKey}`) : filterValues.expenseCategory;

      result.push({
        key: "category",
        label: label,
        icon: CurrencyDollarIcon,
        permanent: false,
      });
    }

    // 10. Tipo (só se includeAdvances for true)
    if (includeAdvances && filterValues.isAdvance !== undefined) {
      result.push({
        key: "type",
        label: filterValues.isAdvance ? "Adiantamento" : "Reembolso",
        icon: TagIcon,
        permanent: false,
      });
    }

    // 11. Reembolso do Cliente
    if (filterValues.clientRefund !== undefined && filterValues.clientRefund !== null) {
      result.push({
        key: "clientRefund",
        label: filterValues.clientRefund ? "Reembolso do Cliente: Sim" : "Reembolso do Cliente: Não",
        icon: TagIcon,
        permanent: false,
      });
    }

    return result;
  }, [filterValues, isRefundRequestPage, loggedUser, includeAdvances, location.pathname]);

  const isChipNonRemovable = useCallback(
    (chipKey: string) => {
      return chipKey === "status" && location.pathname.includes("/approvals") && filterValues.statusFilter?.key === "all";
    },
    [location.pathname, filterValues.statusFilter?.key],
  );

  if (chips.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
        {chips.map((chip) => {
          const Icon = chip.icon;
          const isNonRemovable = chip.permanent || isChipNonRemovable(chip.key);

          return (
            <Chip
              key={chip.key}
              icon={Icon ? <Icon className="w-3 h-3" /> : undefined}
              label={chip.label}
              onDelete={isNonRemovable ? undefined : () => removeFilter(chip.key)}
              deleteIcon={!isNonRemovable ? <XCircleSolidIcon className="w-4 h-4" /> : undefined}
              variant="filled"
              color={isNonRemovable ? "secondary" : "primary"}
              size="small"
              sx={{
                ...(isNonRemovable && {
                  "& .MuiChip-deleteIcon": {
                    display: "none",
                  },
                }),
              }}
            />
          );
        })}
      </Stack>
    </Box>
  );
};

export default ActiveFiltersChips;
